import { useState } from "react";
import { toast } from "react-toastify";
import type { Organization } from "../../hooks/rebuildBackendFetchHooks";
import OrganizationSelector from "../../components/ui/utils/OrganizationSelector";

interface AddManualEstimateReviewModalProps {
  organizations: Organization[];
  onClose: () => void;
  onSubmit: (sourceOrgId: string, sourceEstimateId: string) => Promise<void>;
}

function AddManualEstimateReviewModal({ organizations, onClose, onSubmit }: AddManualEstimateReviewModalProps) {
  const [selectedOrg, setSelectedOrg] = useState<Organization | undefined>();
  const [estimateId, setEstimateId] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const isValidOrg = !!selectedOrg && selectedOrg.organizationId !== "default";
  const isDisabled = isSaving || !estimateId.trim() || !isValidOrg;

  const onSave = async () => {
    if (!selectedOrg) return;
    setIsSaving(true);
    try {
      await onSubmit(selectedOrg.organizationId, estimateId.trim());
    } catch (e) {
      toast.error(e.message || "Failed to create manual estimate review");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-[28rem] relative">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Add Manual Estimate Review</h2>

          <div className="mb-4">
            <OrganizationSelector
              label="Organization"
              organizations={organizations}
              selectedOrganization={selectedOrg}
              onSelect={setSelectedOrg}
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Estimate ID</label>
            <input
              type="text"
              value={estimateId}
              onChange={(e) => setEstimateId(e.target.value)}
              placeholder="Source estimate id"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={isDisabled}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSaving ? "Creating..." : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddManualEstimateReviewModal;
